// src/registerServiceWorker.js
// Robust CRA-era service worker registration that avoids stale bundles
// and bad registrations (HTML served instead of JS, mixed Workbox, etc.).

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/.test(window.location.hostname)
);

// --- defaults (you can override by passing options to register) ---
const DEFAULTS = {
  // If true: when a new SW is installed, we ask it to skipWaiting and
  // reload the page automatically (freshest possible behavior).
  autoReloadOnUpdate: true,

  // How often to ping registration.update() while the tab is visible.
  updateIntervalMs: 5 * 60 * 1000, // 5 minutes

  // Extra guard: validate the SW asset ALWAYS (prod+dev), not just localhost.
  alwaysValidate: true,
};

/**
 * Register the service worker with robust validation and safe update behavior.
 * @param {Object} opts
 * @param {boolean} [opts.autoReloadOnUpdate=true]  Auto-reload when a new version is ready
 * @param {number}  [opts.updateIntervalMs=300000]  Periodic update check while visible
 * @param {boolean} [opts.alwaysValidate=true]      Validate SW response before registering
 */
export default function register(opts = {}) {
  const options = { ...DEFAULTS, ...opts };

  // Quick opt-out for debugging: ?no-sw=1
  const url = new URL(window.location.href);
  if (url.searchParams.get('no-sw') === '1') return;

  // if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;

  // PUBLIC_URL must be same-origin for SW scope to work correctly
  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) return;

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
    

    // Validate in ALL environments, not just localhost
    if (options.alwaysValidate || isLocalhost) {
      checkValidServiceWorker(swUrl, options);
    } else {
      registerValidSW(swUrl, options);
    }
  });
}

function registerValidSW(swUrl, options) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // When a new SW is found, track its lifecycle.
      registration.onupdatefound = () => {
        const installing = registration.installing;
        if (!installing) return;

        installing.onstatechange = () => {
          if (installing.state === 'installed') {
            // If there's an existing controller, this is an update.
            if (navigator.serviceWorker.controller) {
              console.info('[sw] New content is available.');

              if (options.autoReloadOnUpdate) {
                // If the new SW is waiting, tell it to activate immediately.
                if (registration.waiting) {
                  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                }
                // Reload once the new SW takes control.
                navigator.serviceWorker.addEventListener('controllerchange', () => {
                  window.location.reload();
                });
              } else {
                // Hook point: dispatch a custom event so your app can show a "Refresh" toast.
                window.dispatchEvent(new CustomEvent('sw-update-available'));
              }
            } else {
              // First install
              console.info('[sw] Content is cached for offline use.');
            }
          }
        };
      };

      // Proactively check for updates on visibility + at an interval
      const update = () => registration.update().catch(() => { });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') update();
      });
      const iv = setInterval(update, options.updateIntervalMs);
      // If SW goes away (unregister), stop pinging
      navigator.serviceWorker.ready.then(() => {
        // noop – keeps the promise chain alive if you want to add cleanup later
      }).catch(() => clearInterval(iv));

      // Handle messages from the SW (e.g., to support SKIP_WAITING in your SW)
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event?.data === 'reload-window') {
          window.location.reload();
        }
      });
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

/**
 * Validate the SW asset before registering:
 *  - Must be HTTP 200
 *  - Must be JS content (content-type contains "javascript")
 * If invalid, unregister any existing SW and reload.
 */
function checkValidServiceWorker(swUrl, options) {
  fetch(swUrl, { cache: 'no-store' })
    .then((response) => {
      const ok = response && response.ok;
      const ct = (response.headers.get('content-type') || '').toLowerCase();
      const looksLikeJS = ct.includes('javascript');

      if (!ok || !looksLikeJS) {
        // The SW file is missing or not JS (often HTML due to a bad route/CDN).
        console.warn('[sw] Invalid service worker response. Unregistering…', { ok, ct });
        navigator.serviceWorker.ready
          .then((registration) => registration.unregister())
          .then(() => {
            // Clearing caches here is optional; usually your SW handles cleanup.
            // caches?.keys?.().then((keys) => keys.forEach((k) => caches.delete(k)));
            window.location.reload();
          });
      } else {
        registerValidSW(swUrl, options);
      }
    })
    .catch(() => {
      // Offline: carry on; SW may already be installed and serving.
      console.log('[sw] No internet connection. Running in offline mode.');
      // You can still attempt registration so offline clients keep their SW.
      registerValidSW(swUrl, options);
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch(() => { });
  }
}
